import { AppSettings } from "@/settings"

/**
 * Einza graphiQL service class
 *
 * Sets headers and based on the requested method returns the response or throws error
 */
class _EinzaGraphiQLService {
  #config = {}
  #headers = {}

  constructor() {
    this.#config = AppSettings.einzaGraphiQLConfig()
    this.#headers = this.#setHeaders()
  }

  /**
   * Provided the body, fires request and returns promise of the response.json() object
   *
   * @param {string} body
   */
  async getData(body) {
    try {
      const request = await this.#setRequest(body)

      return await request.json()
    } catch (error) {
      throw new Error(error)
    }
  }

  /**
   * Formats the sort object into a string representation.
   *
   * @param {Object} sort - The sort object.
   * @returns {string|null} - The formatted sort string or null if sort is falsy.
   */
  formatSortParam = (sort) => {
    if (!sort) {
      return null
    }

    return `${sort.orderBy}.${sort.direction}`
  }

  /**
   * Forms graphiQL query as a string
   *
   * @param {array} queryParams
   *
   * @returns {string}
   */
  formQuery(queryParams) {
    let query = `{ \n `

    queryParams.forEach((queryParam) => {
      if (!queryParam.method || !queryParam.fields) {
        throw new Error("Invalid query parameter: missing method or fields")
      }

      query += `${queryParam.method}`

      if (queryParam.variables) {
        query += `${this.#stringifyVariables(queryParam.variables)}`
      }

      query += ` { \n`

      queryParam.fields.forEach((field) => {
        query += `${field} \n`
      })

      if (queryParam.expandedFields) {
        query += `${this.#stringifyExpandedFields(queryParam.expandedFields)}`
      }

      query += `} \n`
    })

    query += `}`

    return query
  }

  /**
   * Method to add key and value to the query
   *
   * @param {string} key
   * @param {any} value
   *
   * @returns {string}
   */
  #stringifyKeyAndValue(key, value) {
    if (!key || typeof value === "undefined" || value === null) return ""

    const stringifiedValue = typeof value !== "string" ? value : `"${value}"`

    return `${key}: ${stringifiedValue}`
  }

  /**
   * Stringifies variables for the query
   *
   * @param {object} variables
   *
   * @returns {string}
   */
  #stringifyVariables(variables) {
    const entries = Object.entries(variables)
    if (entries.length === 0) {
      return ""
    }

    const query = entries
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return this.#stringifyArrayValues(key, value)
        }

        return this.#stringifyKeyAndValue(key, value)
      })
      .join(", ")

    return `(${query})`
  }

  /**
   * Stringifies values and key for the query
   *
   * @param {string} key
   * @param {array} values
   */
  #stringifyArrayValues(key, values) {
    if (!Array.isArray(values)) {
      return ""
    }

    const query = values
      .map((mappedValue) => {
        const entries = Object.entries(mappedValue)
        if (entries.length === 0) {
          return ""
        }

        const queryEntries = entries
          .map(([key, value]) => {
            return this.#stringifyKeyAndValue(key, value)
          })
          .join(", ")

        return `{${queryEntries}}`
      })
      .join(", ")

    return `${key}: [${query}]`
  }

  /**
   * Stringifies expanded fields
   *
   * Object example:
   * expandedFields: {
   *   items: ["sortNumber", "itemNumber", "sku"],
   * },
   *
   * @param {object} fields
   *
   * @returns {string}
   */
  #stringifyExpandedFields(fields) {
    const entries = Object.entries(fields)

    let query = ""

    entries.map(([key, value]) => {
      if (!key || key.length <= 0) {
        throw new Error("Invalid expanded field key")
      }

      if (!value || value.length <= 0) {
        throw new Error("Invalid expanded field value")
      }

      query += `${key} { \n`

      value.forEach((item) => {
        if (!item || typeof item !== "string" || item.length <= 0) {
          throw new Error("Invalid expanded field value")
        }

        query += `${item} \n`
      })

      query += `} \n`

      return query
    })

    return query
  }

  /**
   * Sets the headers for the request
   *
   * @return {Headers}
   */
  #setHeaders() {
    const { graphiQLToken } = this.#config

    return new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${graphiQLToken}`,
    })
  }

  /**
   * Sets the request with headers, endpoint and body
   *
   * @param {string} body
   *
   * @return {Promise}
   */
  async #setRequest(body) {
    if (!body || typeof body !== "string") throw new Error("Body structure is invalid")

    const { graphiQLEndpoint } = this.#config
    const request = await fetch(`${graphiQLEndpoint}`, {
      headers: this.#headers,
      method: "POST",
      body: JSON.stringify({ query: body }),
    })

    return request
  }
}

export const EinzaGraphiQLService = new _EinzaGraphiQLService()
